import React from "react";

import { Table } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";

export type SortDirection = "asc" | "desc";

const HEADER_HEIGHT = 44;
/** Default row height. Exported so per-row overrides can stay relative to it. */
export const ROW_HEIGHT = 48;
const EMPTY_STATE_HEIGHT = 356;
const SKELETON_ROW_COUNT = 5;

/** Skeleton rows arrive without an `_id`, which is how a cell renderer spots them. */
export const isLoadingRow = (data: { _id?: string }): boolean => !data._id;

type WorkflowsTableShellProps<T> = {
	testId: string;
	columns: PYColumnDef<T>[];
	rows: T[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
	/**
	 * Per-row height override. Also feeds the wrapper's height, which would otherwise
	 * assume every row is ROW_HEIGHT and clip or overshoot the grid.
	 */
	getRowHeight?: (row: T) => number;
	/** Per-row class, e.g. to suppress the divider between grouped rows. */
	getRowClass?: (row: T) => string | undefined;
	/**
	 * Row identity, defaulting to `_id`. Override when a table synthesises rows whose
	 * `_id` can repeat — duplicate ids make ag-grid drop rows and misroute clicks.
	 */
	getRowId?: (row: T) => string;
};

/**
 * The chrome every Workflows table shares: the height-transition wrapper sized off
 * the row count, the `<Table>` configuration, and the column-state → `onSortChange`
 * translation. Pages supply only their columns and rows.
 *
 * `initialState.sort` applies at mount only, which is what keeps a URL-restored sort
 * showing in the header — don't convert it to a controlled prop without checking the
 * back-button behaviour on all three pages.
 */
export const WorkflowsTableShell = <T extends { _id: string }>({
	testId,
	columns,
	rows,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
	getRowHeight,
	getRowClass,
	getRowId,
}: WorkflowsTableShellProps<T>) => {
	const onSortChanged = (event: SortChangedEvent<T>) => {
		const sortedColumn = event.api.getColumnState().find((col) => col.sort);
		if (sortedColumn?.colId) {
			onSortChange({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as SortDirection,
			});
		}
	};

	const rowsLength = isLoading ? SKELETON_ROW_COUNT : rows.length;
	// Summed rather than multiplied, so a table with mixed row heights still gets a
	// wrapper tall enough for exactly its rows and no blank strip below them.
	const rowsHeight = isLoading
		? SKELETON_ROW_COUNT * ROW_HEIGHT
		: rows.reduce((total, row) => total + (getRowHeight?.(row) ?? ROW_HEIGHT), 0);
	const tableWrapperHeight = rowsLength === 0 ? `${EMPTY_STATE_HEIGHT}px` : `${HEADER_HEIGHT + rowsHeight}px`;

	return (
		<div
			data-testid={testId}
			style={{
				height: tableWrapperHeight,
				transition: "height 150ms ease-out",
			}}
		>
			<Table<T>
				columnDefs={columns}
				rowData={rows}
				loading={isLoading}
				getRowId={(params) => getRowId?.(params.data) ?? params.data._id}
				rowHeight={ROW_HEIGHT}
				{...(getRowHeight
					? { getRowHeight: (params: { data?: T }) => (params.data ? getRowHeight(params.data) : ROW_HEIGHT) }
					: {})}
				{...(getRowClass
					? { getRowClass: (params: { data?: T }) => (params.data ? getRowClass(params.data) : undefined) }
					: {})}
				defaultColDef={{
					cellStyle: { display: "flex", alignItems: "center" },
					resizable: false,
					sortable: false,
					suppressMovable: true,
					sortingOrder: ["asc", "desc"],
				}}
				emptyStateText={emptyStateText}
				onSortChanged={onSortChanged}
				initialState={{
					sort: {
						sortModel: activeSort ? [{ colId: activeSort, sort: activeSortDirection }] : [],
					},
				}}
			/>
		</div>
	);
};

export default WorkflowsTableShell;
